import { useState, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  FileText,
  Info,
} from "lucide-react";

import { api } from "@/api/client";
import { ScreeningDisclaimer, ScreeningUnavailableBanner, FAIL_CLOSED_MESSAGE } from "./screeningShared";

interface BatchRowResult {
  query: string;
  status: string; // clear | potential_match | match | unavailable
  risk_level?: string;
  total_matches?: number;
  highest_score?: number;
  action?: string | null;
  certificate_id?: string;
  error?: string;
}

interface BatchResponse {
  total: number;
  clear: number;
  potential_match: number;
  match: number;
  unavailable: number;
  errors: number;
  fail_closed_note?: string | null;
  results: BatchRowResult[];
}

const MAX_BATCH_ROWS = 100;

/** Parse a screening CSV (header row + data rows) into batch items. */
function parseCsv(text: string, screeningType: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const rows: Record<string, string>[] = [];
  for (const line of lines.slice(1)) {
    const cells = line.split(",").map((c) => c.trim());
    const row: Record<string, string> = {};
    headers.forEach((h, i) => {
      if (cells[i]) row[h] = cells[i];
    });
    // Every screening type needs a primary query column.
    if (screeningType === "goods" ? row.description : row.name) rows.push(row);
  }
  return rows;
}

export default function SanctionsBatchUpload() {
  const { toast } = useToast();
  const [screeningType, setScreeningType] = useState("party");
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [batchResult, setBatchResult] = useState<BatchResponse | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith(".csv")) {
      setFile(droppedFile);
    } else {
      toast({
        title: "Invalid file",
        description: "Please upload a CSV file",
        variant: "destructive",
      });
    }
  }, [toast]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    setBatchResult(null);
    setUnavailable(null);

    try {
      const text = await file.text();
      const rows = parseCsv(text, screeningType);
      if (rows.length === 0) {
        toast({
          title: "No rows found",
          description: "Check the CSV has a header row and at least one data row (see the template).",
          variant: "destructive",
        });
        return;
      }
      let items = rows;
      if (rows.length > MAX_BATCH_ROWS) {
        items = rows.slice(0, MAX_BATCH_ROWS);
        toast({
          title: `Only the first ${MAX_BATCH_ROWS} rows will be screened`,
          description: `Your file has ${rows.length} rows — split it and run the remainder as a second batch.`,
        });
      }

      // Goods rows key on `description`; party/vessel key on `name`.
      const { data } = await api.post<BatchResponse>("/api/sanctions/screen/batch", {
        items,
        screening_type: screeningType,
      });
      setBatchResult(data);

      if (data.unavailable > 0) {
        setUnavailable(
          data.fail_closed_note ||
            `${data.unavailable} row(s) could not be screened — treat them as unscreened, not clear.`,
        );
      }
      toast({
        title: "Batch screening complete",
        description: `${data.total} row(s): ${data.clear} clear, ${data.potential_match} review, ${data.match} match${data.unavailable ? `, ${data.unavailable} unscreened` : ""}`,
        variant: data.match > 0 || data.unavailable > 0 ? "destructive" : undefined,
      });
    } catch (error) {
      // Fail closed — the whole batch is unscreened.
      setUnavailable(FAIL_CLOSED_MESSAGE);
      toast({
        title: "Batch screening unavailable",
        description: "No rows were screened — do not treat any of them as clear.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const templates: Record<string, string> = {
      party: "name,country\nAcme Trading Co,US\nGlobal Imports Ltd,GB\n",
      vessel: "name,imo,mmsi,flag_code\nM/V PACIFIC TRADER,9123456,123456789,PA\n",
      goods: "description,hs_code,destination_country\nIndustrial centrifuge,8421.19,IR\n",
    };

    const blob = new Blob([templates[screeningType]], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sanctions_screening_template_${screeningType}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadResults = () => {
    if (!batchResult) return;

    const esc = (v: unknown) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = "query,status,risk_level,matches,action,reference,note";
    const rows = batchResult.results.map((r) =>
      [r.query, r.status, r.risk_level ?? "", r.total_matches ?? "", r.action ?? "",
       r.certificate_id ?? "", r.error ?? ""].map(esc).join(","),
    );
    const blob = new Blob([[header, ...rows].join("\n") + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `screening_results_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Upload className="w-6 h-6 text-red-400" />
          Batch Screening
        </h1>
        <p className="text-slate-400 mt-1">
          Upload a CSV file to screen multiple entities at once
        </p>
      </div>

      {/* Upload Card */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Upload CSV File</CardTitle>
          <CardDescription className="text-slate-400">
            Screen up to {MAX_BATCH_ROWS} entities per batch
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Screening Type */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-white">Screening Type</label>
            <Select value={screeningType} onValueChange={setScreeningType}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="party" className="text-white">Party / Entity</SelectItem>
                <SelectItem value="vessel" className="text-white">Vessel</SelectItem>
                <SelectItem value="goods" className="text-white">Goods</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? "border-red-500 bg-red-500/10"
                : file
                ? "border-emerald-500/50 bg-emerald-500/5"
                : "border-slate-700 hover:border-slate-600"
            }`}
          >
            {file ? (
              <div className="space-y-2">
                <FileSpreadsheet className="w-12 h-12 text-emerald-400 mx-auto" />
                <p className="text-white font-medium">{file.name}</p>
                <p className="text-sm text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFile(null)}
                  className="text-slate-400 hover:text-white"
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="w-12 h-12 text-slate-500 mx-auto" />
                <div>
                  <p className="text-white font-medium">Drag and drop your CSV file here</p>
                  <p className="text-sm text-slate-400">or click to browse</p>
                </div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <Button
                  variant="outline"
                  className="border-slate-700 text-slate-400 hover:text-white"
                  onClick={() => document.getElementById("file-upload")?.click()}
                >
                  Select File
                </Button>
              </div>
            )}
          </div>

          {/* Template Download */}
          <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-slate-500" />
              <div>
                <p className="text-sm font-medium text-white">Need a template?</p>
                <p className="text-xs text-slate-500">Download our CSV template</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownloadTemplate}
              className="text-red-400 hover:text-red-300"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>

          {/* Upload Button */}
          <Button
            onClick={handleUpload}
            disabled={!file || isUploading}
            className="w-full bg-red-500 hover:bg-red-600 text-white"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Start Batch Screening
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {batchResult && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Screening Complete
                </CardTitle>
                <CardDescription className="text-slate-400">
                  {batchResult.total} row(s) screened against the covered lists
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Results Summary */}
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30 text-center">
                <CheckCircle className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-emerald-400">{batchResult.clear}</p>
                <p className="text-xs text-slate-400">Clear</p>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 text-center">
                <AlertTriangle className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-amber-400">{batchResult.potential_match}</p>
                <p className="text-xs text-slate-400">Review</p>
              </div>
              <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30 text-center">
                <XCircle className="w-5 h-5 text-red-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-red-400">{batchResult.match}</p>
                <p className="text-xs text-slate-400">Match</p>
              </div>
              <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/30 text-center">
                <Info className="w-5 h-5 text-orange-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-orange-400">{batchResult.unavailable}</p>
                <p className="text-xs text-slate-400">Unscreened</p>
              </div>
            </div>

            {/* Per-row results */}
            <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-800">
              <table className="w-full text-sm">
                <thead className="bg-slate-800/70 text-slate-400 text-left sticky top-0">
                  <tr>
                    <th className="px-3 py-2 font-medium">Query</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Risk</th>
                    <th className="px-3 py-2 font-medium">Matches</th>
                  </tr>
                </thead>
                <tbody>
                  {batchResult.results.map((row, idx) => (
                    <tr key={idx} className="border-t border-slate-800">
                      <td className="px-3 py-2 text-white truncate max-w-[220px]">{row.query}</td>
                      <td className="px-3 py-2">
                        <Badge className={
                          row.status === "clear" ? "bg-emerald-500/20 text-emerald-400"
                          : row.status === "match" ? "bg-red-500/20 text-red-400"
                          : row.status === "potential_match" ? "bg-amber-500/20 text-amber-400"
                          : "bg-orange-500/20 text-orange-400"
                        }>
                          {row.status === "unavailable" ? "unscreened" : row.status.replace("_", " ")}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-slate-400">{row.risk_level ?? "—"}</td>
                      <td className="px-3 py-2 text-slate-400">{row.total_matches ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleDownloadResults}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Results (CSV)
              </Button>
              <Button
                variant="outline"
                className="border-slate-700 text-slate-400 hover:text-white"
                onClick={() => {
                  setBatchResult(null);
                  setUnavailable(null);
                  setFile(null);
                }}
              >
                New Batch
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <ScreeningUnavailableBanner message={unavailable} />

      <ScreeningDisclaimer />

      {/* Instructions */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white text-sm">CSV Format Requirements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <h4 className="font-medium text-white mb-2">Party Screening</h4>
              <code className="text-xs text-slate-400 block">
                name,country<br />
                Acme Trading,US<br />
                Global Corp,GB
              </code>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <h4 className="font-medium text-white mb-2">Vessel Screening</h4>
              <code className="text-xs text-slate-400 block">
                name,imo,flag_code<br />
                M/V STAR,9123456,PA<br />
                MT OCEAN,9876543,LR
              </code>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <h4 className="font-medium text-white mb-2">Goods Screening</h4>
              <code className="text-xs text-slate-400 block">
                description,hs_code,destination<br />
                Centrifuge,8421.19,IR<br />
                Electronics,8542.31,CN
              </code>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

